import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { AppSnackbar } from '@/shared/ui';
import { appendPendingSupportCode } from '@/shared/utils/apiError';

export type SnackbarSeverity = 'success' | 'error' | 'warning' | 'info';

export interface SnackbarContextValue {
  showSnackbar: (message: string, severity?: SnackbarSeverity) => void;
  closeSnackbar: () => void;
}

type SnackbarHandler = (message: string, severity?: SnackbarSeverity) => void;

/** Imperative handler for callers outside React (AuthContext, axios interceptors). */
let imperativeSnackbarHandler: SnackbarHandler | null = null;

/** Show a snackbar from non-React code. No-ops until SnackbarProvider mounts. */
export function showSnackbar(message: string, severity: SnackbarSeverity = 'success'): void {
  if (imperativeSnackbarHandler) {
    imperativeSnackbarHandler(message, severity);
    return;
  }
  if (import.meta.env.DEV) {
    console.warn('[snackbar] SnackbarProvider not mounted; skipped:', message);
  }
}

const SnackbarContext = createContext<SnackbarContextValue | undefined>(undefined);

interface SnackbarProviderProps {
  children: ReactNode;
}

export const SnackbarProvider: React.FC<SnackbarProviderProps> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState<SnackbarSeverity>('success');
  const [autoHideDuration, setAutoHideDuration] = useState(6000);

  const deferredTimerRef = useRef<number | null>(null);

  const showSnackbarInProvider = useCallback(
    (nextMessage: string, nextSeverity: SnackbarSeverity = 'success'): void => {
      const apply = (): void => {
        const appended = nextSeverity === 'error'
          ? appendPendingSupportCode(nextMessage)
          : { text: nextMessage, pinned: false };
        setMessage(appended.text);
        setSeverity(nextSeverity);
        setAutoHideDuration(appended.pinned ? 20000 : 6000);
        setOpen(true);
      };

      // Errors stay on the screen that failed. Success/info/warning usually
      // follow a close or a navigation scheduled in the same turn — paint that
      // first, then the toast (setTimeout runs after the React commit).
      if (nextSeverity === 'error') {
        if (deferredTimerRef.current != null) {
          window.clearTimeout(deferredTimerRef.current);
          deferredTimerRef.current = null;
        }
        apply();
        return;
      }

      if (deferredTimerRef.current != null) {
        window.clearTimeout(deferredTimerRef.current);
      }
      deferredTimerRef.current = window.setTimeout(() => {
        deferredTimerRef.current = null;
        apply();
      }, 0);
    },
    []
  );

  useEffect(() => {
    return () => {
      if (deferredTimerRef.current != null) {
        window.clearTimeout(deferredTimerRef.current);
      }
    };
  }, []);

  const closeSnackbar = useCallback((): void => {
    setOpen(false);
  }, []);

  useEffect(() => {
    imperativeSnackbarHandler = showSnackbarInProvider;
    return () => {
      if (imperativeSnackbarHandler === showSnackbarInProvider) {
        imperativeSnackbarHandler = null;
      }
    };
  }, [showSnackbarInProvider]);

  const value = useMemo(
    () => ({ showSnackbar: showSnackbarInProvider, closeSnackbar }),
    [showSnackbarInProvider, closeSnackbar]
  );

  return (
    <SnackbarContext.Provider value={value}>
      {children}
      <AppSnackbar
        open={open}
        message={message}
        severity={severity}
        onClose={closeSnackbar}
        autoHideDuration={autoHideDuration}
      />
    </SnackbarContext.Provider>
  );
};

export function useSnackbar(): SnackbarContextValue {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error('useSnackbar must be used within a SnackbarProvider');
  }
  return context;
}
